import { Link } from "react-router-dom";
import { Video, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { ProgressiveImage } from "@/components/ui/progressive-image";
import { storage } from "@/lib/storage";
import { getExternalLinkLabel } from "@/lib/utils/external-video-link";
import { formatClassDateCompact, formatClassTimeRange } from "@/lib/utils/class-time-utils";
import type { ClassVideoSummary } from "@/types/class-video-summary";

interface ClassVideoCardProps {
  data: ClassVideoSummary;
  isOwner: boolean;
}

export function ClassVideoCard({ data, isOwner }: ClassVideoCardProps) {
  const hasExternalLink = !!data.external_video_url;
  const isExpired = data.all_expired && !hasExternalLink;
  const hasNoContent = data.total_video_count === 0 && !hasExternalLink;
  const isClickable = isOwner || (!isExpired && !hasNoContent);

  const coverUrl =
    data.cover_photo_url ||
    (data.is_studio_managed && data.room_photo_url) ||
    null;

  const dateStr = formatClassDateCompact(data.date);
  const timeStr = formatClassTimeRange(data.start_time, data.end_time);

  // Badge content
  let badgeContent: React.ReactNode;
  if (hasNoContent && !isOwner) {
    badgeContent = (
      <span className="text-[10px] font-semibold text-on-dark-muted">No Videos</span>
    );
  } else if (hasNoContent && isOwner) {
    badgeContent = (
      <span className="text-[10px] font-semibold text-on-dark-muted">Add Videos</span>
    );
  } else if (isExpired) {
    badgeContent = (
      <span className="text-[10px] font-semibold text-red-400">Expired</span>
    );
  } else if (hasExternalLink) {
    const linkInfo = getExternalLinkLabel(data.external_video_url!);
    badgeContent = (
      <>
        {linkInfo.iconUrl ? (
          <img src={linkInfo.iconUrl} alt="" className="h-3 w-3" />
        ) : (
          <ExternalLink className="h-3 w-3 text-on-dark" />
        )}
        <span className="text-[10px] font-semibold text-on-dark">{linkInfo.label}</span>
      </>
    );
  } else {
    badgeContent = (
      <>
        <Video className="h-3 w-3 text-on-dark" />
        <span className="text-[10px] font-semibold text-on-dark">{data.video_count}</span>
      </>
    );
  }

  const getHref = (): string | undefined => {
    if (hasNoContent && !isOwner) return undefined;
    if (isExpired && !isOwner) return undefined;
    if (hasExternalLink && !isOwner) return undefined; // handled via onClick
    return `/classes/${data.id}/videos`;
  };

  const handleClick = () => {
    if (!isOwner && hasExternalLink && data.external_video_url) {
      window.open(data.external_video_url, "_blank", "noopener,noreferrer");
    }
  };

  const href = getHref();

  const cardContent = (
    <Card
      className={`relative overflow-hidden p-0 ${
        isClickable ? "group cursor-pointer" : "cursor-default"
      } ${isExpired && !isOwner ? "opacity-50" : ""}`}
      onClick={!href && isClickable ? handleClick : undefined}
    >
      <div className="relative aspect-video">
        {/* Background */}
        {coverUrl ? (
          <>
            <div className="absolute inset-0 transition-transform duration-300 group-hover:scale-105">
              <ProgressiveImage
                src={coverUrl}
                thumbnailSrc={storage.getThumbnailUrl(coverUrl, {
                  width: 400,
                  height: 225,
                  quality: 30,
                })}
                alt={data.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-black/40" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-jeans/20 via-diamond/10 to-purple-500/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Badge */}
        <div className="absolute top-2 right-2 z-10 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1">
          {badgeContent}
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
          <p className="text-on-dark font-semibold text-sm leading-tight line-clamp-2">
            {data.title}
          </p>
          <p className="text-on-dark-muted text-xs mt-0.5">
            {dateStr} · {timeStr}
          </p>
        </div>
      </div>
    </Card>
  );

  if (href && isClickable) {
    return (
      <Link to={href} state={{ fromGallery: true }} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
